import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2, X, Save, Sparkles, Eye, EyeOff } from "lucide-react";
import ImageUpload from "../../components/cms/ImageUpload";
import RichTextEditor from "../../components/cms/RichTextEditor";
import MembersImportBar from "../../components/cms/MembersImportBar";

// Convert an ISO datetime string (e.g. "2026-11-18T09:30:00Z") into the value
// format expected by <input type="datetime-local"> ("2026-11-18T09:30").
// Returns "" when the input is empty or unparsable, so the picker starts blank.
function isoToLocal(iso) {
  if (!iso) return "";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local) {
  if (!local) return "";
  const d = new Date(local);
  return isNaN(d.getTime()) ? local : d.toISOString();
}

// URL-safe slugifier. Mirrors backend `slugify()` so what the user types is
// exactly what the backend will store. Called on blur of the slug input so
// dirty pastes ("Éxito del Desayuno-Debate | 2026") auto-clean before save.
function slugify(value) {
  if (!value) return "";
  return value
    .normalize("NFKD")
    // Strip all non-ASCII (accents, emojis, CJK, RTL marks)
    .replace(/[\u0080-\uFFFF]/g, "")
    // Collapse anything that isn't a letter or digit into a single dash
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

// Slug editor with live availability check. Extracted so the debounce timer,
// abort controller, and "checking / available / taken" state stay isolated
// from the parent form's re-renders.
function SlugField({ editing, set, rKey }) {
  const [status, setStatus] = useState("idle"); // idle | checking | ok | taken
  useEffect(() => {
    const raw = editing.slug ?? "";
    const normalized = slugify(raw);
    if (!normalized) { setStatus("idle"); return; }
    setStatus("checking");
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ resource: rKey, slug: normalized });
      if (editing.id) params.set("exclude_id", editing.id);
      api.get(`/admin/slug-check?${params}`, { signal: ctl.signal })
        .then((r) => setStatus(r.data?.available ? "ok" : "taken"))
        .catch((e) => { if (e.name !== "CanceledError" && e.name !== "AbortError") setStatus("idle"); });
    }, 400);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [editing.slug, editing.id, rKey]);

  const badgeClasses = {
    checking: "text-gray-400",
    ok: "text-emerald-600",
    taken: "text-amber-600",
    idle: "text-gray-300",
  }[status];
  const badgeText = {
    checking: "…",
    ok: "✓ libre",
    taken: "⚠ ya existe",
    idle: "",
  }[status];

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={editing.slug ?? ""}
          onChange={(e) => set(e.target.value)}
          onBlur={(e) => set(slugify(e.target.value))}
          className="flex-1 px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm font-mono"
          data-testid="field-slug"
          placeholder="ej. mi-evento-2026"
        />
        {editing.title_es ? (
          <button
            type="button"
            onClick={() => set(slugify(editing.title_es))}
            className="px-3 py-2 border border-gray-200 text-xs uppercase tracking-wide text-gray-700 hover:bg-gray-50"
            data-testid="field-slug-auto"
            title="Generar slug desde el título en español"
          >
            Auto
          </button>
        ) : null}
      </div>
      {badgeText ? (
        <p className={`text-xs mt-1 ${badgeClasses}`} data-testid="field-slug-status">
          {badgeText}
        </p>
      ) : null}
    </div>
  );
}

const RESOURCE_DEFS = {
  pages: {
    title: "Páginas",
    blank: () => ({ slug: "", title_es: "", title_fr: "", subtitle_es: "", subtitle_fr: "", body_es: "", body_fr: "", hero_image: "" }),
    fields: [
      { k: "slug", label: "Slug *" },
      { k: "title_es", label: "Título (ES)" },
      { k: "title_fr", label: "Título (FR)" },
      { k: "subtitle_es", label: "Subtítulo (ES)" },
      { k: "subtitle_fr", label: "Subtítulo (FR)" },
      { k: "hero_image", label: "URL imagen hero" },
      { k: "body_es", label: "Cuerpo (ES)", textarea: true, rows: 8 },
      { k: "body_fr", label: "Cuerpo (FR)", textarea: true, rows: 8 },
    ],
    rowTitle: (r) => r.title_es || r.slug,
    rowSub: (r) => `/${r.slug}`,
  },
  news: {
    title: "Noticias",
    blank: () => ({ slug: "", title_es: "", title_fr: "", title_en: "", title_ar: "", excerpt_es: "", excerpt_fr: "", excerpt_en: "", excerpt_ar: "", body_es: "", body_fr: "", body_en: "", body_ar: "", meta_description_es: "", meta_description_fr: "", meta_description_en: "", meta_description_ar: "", cover_image: "", tag: "", published: true }),
    // Tabbed bilingual editor: base fields on top, ES/FR tabs below.
    bilingual: true,
    baseFields: [
      { k: "slug", label: "Slug *" },
      { k: "tag", label: "Etiqueta" },
      { k: "cover_image", label: "Imagen portada (16:9)", type: "image", aspect: "video", cropAspect: 16 / 9 },
      { k: "published", label: "Publicado", type: "checkbox" },
    ],
    langFields: (lang, L) => [
      { k: `title_${lang}`, label: `${L.titulo} (${lang.toUpperCase()})` },
      { k: `excerpt_${lang}`, label: `${L.resumen} (${lang.toUpperCase()})`, textarea: true, rows: 3 },
      { k: `body_${lang}`, label: `${L.cuerpo} (${lang.toUpperCase()})`, richtext: true },
      { k: `meta_description_${lang}`, label: `Meta descripción SEO (${lang.toUpperCase()}) · 150-160 caracteres`, textarea: true, rows: 2 },
    ],
    rowTitle: (r) => r.title_es,
    rowSub: (r) => r.tag,
  },
  events: {
    title: "Eventos",
    blank: () => ({ slug: "", title_es: "", title_fr: "", title_en: "", title_ar: "", description_es: "", description_fr: "", description_en: "", description_ar: "", meta_description_es: "", meta_description_fr: "", meta_description_en: "", meta_description_ar: "", cover_image: "", location: "", starts_at: "", ends_at: "", category: "camara", upcoming: true, published: true, show_home_popup: false, popup_headline_es: "", popup_headline_fr: "", popup_headline_en: "", popup_headline_ar: "", popup_cta_label_es: "", popup_cta_label_fr: "", popup_cta_label_en: "", popup_cta_label_ar: "", popup_cta_url: "" }),
    bilingual: true,
    baseFields: [
      { k: "slug", label: "Slug *" },
      { k: "category", label: "Categoría", select: ["camara", "socios", "ferias_es", "ferias_ma", "actividad", "encuentros", "networking", "seminarios", "salones_ferias", "visitas_reuniones", "actos_institucionales", "webinars"] },
      { k: "cover_image", label: "Imagen portada (16:9)", type: "image", aspect: "video", cropAspect: 16 / 9 },
      { k: "location", label: "Ubicación" },
      { k: "starts_at", label: "Inicio", type: "datetime" },
      { k: "ends_at", label: "Fin", type: "datetime" },
      { k: "upcoming", label: "Próximo", type: "checkbox" },
      { k: "published", label: "Publicado", type: "checkbox" },
      { k: "show_home_popup", label: "Mostrar popup en Home (con cuenta atrás)", type: "checkbox" },
      { k: "popup_cta_url", label: "URL botón popup (vacío = página de la actividad)" },
    ],
    langFields: (lang, L) => [
      { k: `title_${lang}`, label: `${L.titulo} (${lang.toUpperCase()})` },
      { k: `description_${lang}`, label: `${L.descripcion} (${lang.toUpperCase()})`, richtext: true },
      { k: `meta_description_${lang}`, label: `Meta descripción SEO (${lang.toUpperCase()}) · 150-160 caracteres`, textarea: true, rows: 2 },
      { k: `popup_headline_${lang}`, label: `Popup — titular (${lang.toUpperCase()}, vacío = título)`, textarea: true, rows: 2 },
      { k: `popup_cta_label_${lang}`, label: `Popup — texto del botón (${lang.toUpperCase()})` },
    ],
    rowTitle: (r) => r.title_es,
    rowSub: (r) => `${r.category} · ${r.location || ""}`,
  },
  members: {
    title: "Socios",
    blank: () => ({ name: "", slug: "", tier: "standard", sector: "", description_es: "", description_fr: "", logo_url: "", website: "", email: "", phone: "", address: "", featured: false }),
    fields: [
      { k: "name", label: "Nombre *" },
      { k: "slug", label: "Slug *" },
      { k: "tier", label: "Categoría", select: ["basico", "standard", "premium"] },
      { k: "sector", label: "Sector" },
      { k: "logo_url", label: "Logo", type: "image", aspect: "logo" },
      { k: "website", label: "Sitio web" },
      { k: "email", label: "Email" },
      { k: "phone", label: "Teléfono" },
      { k: "address", label: "Dirección" },
      { k: "description_es", label: "Descripción (ES)", textarea: true },
      { k: "description_fr", label: "Descripción (FR)", textarea: true },
      { k: "featured", label: "Destacado", type: "checkbox" },
    ],
    rowTitle: (r) => r.name,
    rowSub: (r) => `${r.tier} · ${r.sector || ""}`,
  },
};

// Bilingual UI labels — kept here (not in i18n) to avoid coupling the CMS to
// public-site translations.
const LANG_LABELS = { titulo: "Título", resumen: "Resumen", cuerpo: "Cuerpo", descripcion: "Descripción" };

// ---- Field renderers --------------------------------------------------------
function Field({ f, editing, setEditing, rKey }) {
  const set = (v) => setEditing((prev) => ({ ...prev, [f.k]: v }));

  if (f.type === "image") {
    return (
      <ImageUpload
        label={f.label}
        aspect={f.aspect || "video"}
        cropAspect={f.cropAspect}
        value={editing[f.k] ?? ""}
        onChange={set}
      />
    );
  }
  if (f.richtext) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{f.label}</label>
        <RichTextEditor
          testId={`field-${f.k}`}
          value={editing[f.k] ?? ""}
          onChange={set}
          placeholder="Comience a escribir…"
        />
      </div>
    );
  }
  return (
    <>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{f.label}</label>
      {f.type === "checkbox" ? (
        <input type="checkbox" checked={!!editing[f.k]} onChange={(e) => set(e.target.checked)} className="accent-[#C8102E]" data-testid={`field-${f.k}`} />
      ) : f.type === "datetime" ? (
        <input
          type="datetime-local"
          value={isoToLocal(editing[f.k])}
          onChange={(e) => set(localToIso(e.target.value))}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
          data-testid={`field-${f.k}`}
        />
      ) : f.select ? (
        <select value={editing[f.k] ?? ""} onChange={(e) => set(e.target.value)} className="w-full px-3 py-2 border border-gray-200 outline-none text-sm bg-white" data-testid={`field-${f.k}`}>
          {f.select.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : f.textarea ? (
        <textarea value={editing[f.k] ?? ""} onChange={(e) => set(e.target.value)} rows={f.rows || 3} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" data-testid={`field-${f.k}`} />
      ) : f.k === "slug" ? (
        // Slug gets: (a) live sanitisation on blur so what you see is what
        // gets stored, (b) an "auto from title" button, (c) live availability
        // check debounced 400ms after typing. Backend also slugifies on write.
        <SlugField editing={editing} set={set} rKey={rKey} />
      ) : (
        <input value={editing[f.k] ?? ""} onChange={(e) => set(e.target.value)} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" data-testid={`field-${f.k}`} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
export default function ResourceAdmin({ resource, apiResource, title, categoryFilter, categoryOptions, defaultCategory }) {
  const rKey = apiResource || resource;
  const def = { ...RESOURCE_DEFS[rKey] };
  if (title) def.title = title;
  const applyCategoryOptions = (fields) =>
    fields ? fields.map((f) => (f.k === "category" && f.select ? { ...f, select: categoryOptions } : f)) : fields;
  if (categoryOptions) {
    if (def.fields) def.fields = applyCategoryOptions(def.fields);
    if (def.baseFields) def.baseFields = applyCategoryOptions(def.baseFields);
  }
  if (defaultCategory) {
    const originalBlank = def.blank;
    def.blank = () => ({ ...originalBlank(), category: defaultCategory });
  }

  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("es");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [aiCfg, setAiCfg] = useState({ enabled: false, ready: false });
  const [showPreview, setShowPreview] = useState(false);

  const load = () =>
    api
      .get(`/admin/${rKey}`)
      .then((r) => {
        const filtered = categoryFilter ? r.data.filter((x) => categoryFilter.includes(x.category)) : r.data;
        setItems(filtered);
      })
      .catch(() => {});

  useEffect(() => { load(); setEditing(null); }, [rKey, JSON.stringify(categoryFilter)]);

  // Load AI translation config once so we can show or hide the button.
  useEffect(() => {
    api.get("/settings")
      .then((r) => setAiCfg({ enabled: !!r.data.ai_translate_enabled, ready: true }))
      .catch(() => setAiCfg({ enabled: false, ready: true }));
  }, []);

  const translateFromOtherLang = async () => {
    // Always translate from Spanish (the canonical source) into the current
    // tab language. Fills every langField of the active tab.
    const target = tab;
    if (target === "es") return;
    const targetFields = isBilingual ? def.langFields(target, LANG_LABELS) : [];
    setTranslating(true);
    setError("");
    try {
      for (const f of targetFields) {
        const srcKey = f.k.replace(/_(es|fr|en|ar)$/, "_es");
        const srcText = (editing[srcKey] || "").trim();
        if (!srcText) continue;
        const { data } = await api.post("/admin/translate", {
          text: srcText, source_lang: "es", target_lang: target,
        });
        setEditing((prev) => ({ ...prev, [f.k]: data.translated }));
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTranslating(false);
    }
  };

  const open = (row) => { setEditing({ ...row }); setTab("es"); setError(""); };
  const close = () => { setEditing(null); setError(""); };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...body } = editing;
        await api.patch(`/admin/${rKey}/${id}`, body);
      } else {
        await api.post(`/admin/${rKey}`, editing);
      }
      close();
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/admin/${rKey}/${id}`);
    load();
  };

  const isBilingual = !!def.bilingual;
  const esFields = useMemo(() => (isBilingual ? def.langFields("es", LANG_LABELS) : []), [isBilingual, def]);
  const frFields = useMemo(() => (isBilingual ? def.langFields("fr", LANG_LABELS) : []), [isBilingual, def]);
  const baseFields = isBilingual ? def.baseFields : def.fields;

  // Live preview shows the richtext content of the current tab, matching how
  // it will look on the public site (uses the same `.prose-cecit-article` CSS).
  const currentLangFields = tab === "es" ? esFields : frFields;
  const previewRichKey = editing && currentLangFields.find((f) => f.richtext)?.k;
  const previewTitleKey = editing && currentLangFields.find((f) => !f.richtext && (f.k.startsWith("title_")))?.k;
  const previewHtml = editing && previewRichKey ? editing[previewRichKey] || "" : "";
  const previewTitle = editing && previewTitleKey ? editing[previewTitleKey] || "" : "";
  const hasPreviewableContent = isBilingual && !!previewRichKey;

  return (
    <div data-testid={`cms-${resource}`}>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040]">{def.title}</h1>
          <p className="text-sm text-gray-600">Gestión bilingüe (ES / FR).</p>
        </div>
        <button onClick={() => open(def.blank())} data-testid={`${resource}-add`} className="bg-[#C8102E] text-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#A30D25]">
          <Plus className="w-4 h-4" /> Nuevo
        </button>
      </div>

      {resource === "members" && <MembersImportBar onImported={load} />}

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-5 py-3">Título</th>
              <th className="text-left px-5 py-3">Detalle</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={3} className="text-center py-12 text-gray-400">Sin elementos.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`${resource}-row-${it.id}`}>
                <td className="px-5 py-3 font-medium text-[#0F2040]">{def.rowTitle(it)}</td>
                <td className="px-5 py-3 text-gray-500 text-xs">{def.rowSub(it)}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => open(it)} className="px-3 py-1.5 text-xs border border-gray-200 hover:border-[#0F2040] mr-2">Editar</button>
                  <button onClick={() => remove(it.id)} className="p-1.5 text-gray-400 hover:text-red-600 inline-block align-middle">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-center" onClick={close}>
          <div
            className={`bg-white ${showPreview && hasPreviewableContent ? "max-w-7xl" : isBilingual ? "max-w-5xl" : "max-w-3xl"} w-full my-4 mx-4 flex flex-col max-h-[calc(100vh-2rem)]`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${resource}-editor`}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="font-semibold text-[#0F2040]">{editing.id ? "Editar" : "Nuevo"} — {def.title}</h2>
              <div className="flex items-center gap-1">
                {hasPreviewableContent && (
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    data-testid={`${resource}-toggle-preview`}
                    title={showPreview ? "Ocultar vista previa" : "Mostrar vista previa en vivo"}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border transition-colors ${
                      showPreview
                        ? "bg-[#0F2040] text-white border-[#0F2040]"
                        : "text-[#0F2040] border-gray-200 hover:border-[#0F2040]"
                    }`}
                  >
                    {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    Vista previa
                  </button>
                )}
                <button onClick={close} className="p-1 hover:bg-gray-100 rounded ml-2"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className={`flex-1 min-h-0 overflow-hidden ${showPreview && hasPreviewableContent ? "grid md:grid-cols-2 divide-x divide-gray-200" : "flex"}`}>
              <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-0">
              {/* Base fields (non-bilingual): slug, category, cover, dates, flags */}
              <div className="grid md:grid-cols-2 gap-4">
                {baseFields.map((f) => (
                  <div key={f.k} className={f.type === "image" || f.textarea || f.richtext ? "md:col-span-2" : ""}>
                    <Field f={f} editing={editing} setEditing={setEditing} rKey={rKey} />
                  </div>
                ))}
              </div>

              {/* Language tabs — only for bilingual resources */}
              {isBilingual && (
                <>
                  <div className="border-b border-gray-200 flex items-center gap-1 flex-wrap" data-testid={`${resource}-lang-tabs`}>
                    {[
                      { k: "es", label: "Español" },
                      { k: "fr", label: "Français" },
                      { k: "en", label: "English" },
                      { k: "ar", label: "العربية" },
                    ].map((t) => (
                      <button
                        key={t.k}
                        type="button"
                        onClick={() => setTab(t.k)}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                          tab === t.k
                            ? "border-[#C8102E] text-[#0F2040]"
                            : "border-transparent text-gray-500 hover:text-[#0F2040]"
                        }`}
                        data-testid={`${resource}-tab-${t.k}`}
                      >
                        {t.label}
                      </button>
                    ))}
                    {aiCfg.enabled && (
                      <button
                        type="button"
                        onClick={translateFromOtherLang}
                        disabled={translating}
                        title={`Rellenar los campos ${tab.toUpperCase()} vacíos traduciendo desde ${tab === "es" ? "FR" : "ES"} con IA`}
                        data-testid={`${resource}-ai-translate`}
                        className="ml-auto mr-1 mb-1 inline-flex items-center gap-1.5 text-xs text-[#C8102E] border border-[#C8102E]/40 px-3 py-1.5 hover:bg-[#C8102E] hover:text-white transition-colors disabled:opacity-50"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {translating ? "Traduciendo…" : `Traducir con IA → ${tab.toUpperCase()}`}
                      </button>
                    )}
                  </div>

                  {["es", "fr", "en", "ar"].map((L) => tab === L && (
                    <div key={L} className="space-y-4" data-testid={`${resource}-pane-${L}`} dir={L === "ar" ? "rtl" : "ltr"}>
                      {def.langFields(L, LANG_LABELS).map((f) => (<div key={f.k}><Field f={f} editing={editing} setEditing={setEditing} /></div>))}
                    </div>
                  ))}
                </>
              )}
              </div>

              {showPreview && hasPreviewableContent && (
                <div className="overflow-y-auto bg-gray-50 p-6 min-h-0" data-testid={`${resource}-preview`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                    <Eye className="w-3 h-3" /> Vista previa · {tab.toUpperCase()}
                  </div>
                  {editing.cover_image && (
                    <img src={editing.cover_image} alt="" className="w-full aspect-video object-cover mb-5" />
                  )}
                  {previewTitle && (
                    <h1 className="font-serif text-2xl md:text-3xl text-[#0F2040] mb-4 leading-tight">
                      {previewTitle}
                    </h1>
                  )}
                  {previewHtml ? (
                    <div
                      className="prose-cecit-article"
                      // Sanitize before injecting: strips scripts, event handlers,
                      // javascript: URLs, iframes, etc. Keeps rich-text formatting
                      // and safe inline styles used by Tiptap.
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(previewHtml, { USE_PROFILES: { html: true } }),
                      }}
                    />
                  ) : (
                    <p className="text-sm text-gray-400 italic">Sin contenido en {tab.toUpperCase()}. Empieza a escribir para verlo aquí.</p>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
              {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
              <div className="flex gap-2">
                <button onClick={close} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancelar</button>
                <button onClick={save} disabled={saving} data-testid={`${resource}-save`} className="bg-[#0F2040] text-white px-5 py-2 text-sm flex items-center gap-2 hover:bg-[#1A2F5C] disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
